import React, { SyntheticEvent, useCallback } from 'react';
import { useToast, SquareButton } from '@app/core';
import { createUseStyles } from 'react-jss';
import { Theme } from '@app/theme';
import { Document, useGetViewDocumentUrl, useRemoveDocument } from '@app/api';

const useStyles = createUseStyles((theme: Theme) => ({
  container: {
    border: {
      color: theme.colors.grayBorder,
      style: 'solid',
      width: 1,
    },
    padding: theme.marginBase * 2,
    borderRadius: theme.borderRadius.std,
    backgroundColor: theme.colors.white,

    display: 'flex',
    alignItems: 'center',
    gap: theme.marginBase * 2,
  },
  fileName: {
    ...theme.fonts.caption,
    flex: 1,
  },
  progress: {
    '-webkit-appearance': 'progress-bar',
    border: 'none',
    borderRadius: 4,
    height: 4,
    width: 120,
    backgroundColor: theme.colors.blueMuted,
    color: theme.colors.red,

    '&::-webkit-progress-bar': {
      backgroundColor: theme.colors.blueMuted,
    },

    '&::-webkit-progress-value': {
      backgroundColor: theme.colors.blue,
    },

    '&::-moz-progress-bar': {
      backgroundColor: theme.colors.blue,
    },
  },
}));

interface Props {
  progress?: number;
  document: Document;
}

export const UploadedDocument = ({ document, progress }: Props) => {
  const classes = useStyles();
  const toast = useToast();

  const { mutateAsync: getUrl } = useGetViewDocumentUrl(document);
  const { mutateAsync: removeDocument } = useRemoveDocument(document);

  const remove = useCallback(async () => {
    try {
      await removeDocument();
    } catch (e) {
      toast.exception(e);
    }
  }, [removeDocument, toast]);

  const view = useCallback(async () => {
    const { url } = await getUrl();
    window.open(url, '_blank', 'noopener noreferrer');
  }, [document, getUrl]);

  return (
    <div className={classes.container}>
      <div className={classes.fileName}>{document.originalName}</div>
      {progress !== undefined && (
        <progress value={progress} max={100} className={classes.progress} />
      )}
      <SquareButton
        icon="ri-eye-line"
        color="blue"
        onClick={view}
        variant="transparent"
        small
        disabled={!document.uploadConfirmed}
      />
      <SquareButton
        icon="ri-delete-bin-line"
        color="red"
        onClick={remove}
        variant="transparent"
        small
      />
    </div>
  );
};
